import * as dayjs from 'dayjs';

/**
 * Period presets shared by the screens that filter a list by date. Kept in one
 * place so the superadmin sales column and the member entry log cannot drift
 * apart on what "last 3 months" means.
 */
export type PeriodPreset =
  | 'thisMonth'
  | 'lastMonth'
  | 'last3Months'
  | 'last6Months'
  | 'thisYear'
  | 'lastYear'
  | 'all'
  | 'custom';

/** Null on either side means unbounded in that direction. */
export interface PeriodRange {
  from: string | null;
  to: string | null;
}

/** An NgbDateStruct, as produced by app-date-range-picker. */
export interface NgbLikeDate {
  year: number;
  month: number;
  day: number;
}

export interface CustomPeriodRange {
  startDate: NgbLikeDate;
  endDate: NgbLikeDate;
}

function ngbDateToDayjs(date: NgbLikeDate): dayjs.Dayjs {
  return dayjs(new Date(date.year, date.month - 1, date.day));
}

/**
 * Resolves a preset to an ISO range. `custom` falls back to the current month
 * until both ends of the range are picked, so a half-finished selection never
 * queries an open range.
 */
export function periodRange(
  preset: PeriodPreset,
  custom?: CustomPeriodRange
): PeriodRange {
  const now = dayjs();
  const thisMonth = {
    from: now.startOf('month').toISOString(),
    to: now.toISOString(),
  };

  switch (preset) {
    case 'lastMonth':
      return {
        from: now.subtract(1, 'month').startOf('month').toISOString(),
        to: now.subtract(1, 'month').endOf('month').toISOString(),
      };
    case 'last3Months':
      return {
        from: now.subtract(3, 'month').startOf('day').toISOString(),
        to: now.toISOString(),
      };
    case 'last6Months':
      return {
        from: now.subtract(6, 'month').startOf('day').toISOString(),
        to: now.toISOString(),
      };
    case 'thisYear':
      return { from: now.startOf('year').toISOString(), to: now.toISOString() };
    case 'lastYear':
      return {
        from: now.subtract(1, 'year').startOf('year').toISOString(),
        to: now.subtract(1, 'year').endOf('year').toISOString(),
      };
    case 'all':
      return { from: null, to: null };
    case 'custom':
      if (custom?.startDate && custom?.endDate) {
        return {
          from: ngbDateToDayjs(custom.startDate).startOf('day').toISOString(),
          to: ngbDateToDayjs(custom.endDate).endOf('day').toISOString(),
        };
      }
      return thisMonth;
    default:
      return thisMonth;
  }
}
